import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setPageTitle } from 'store/actions/header';
import { PageContentWrapper } from 'app/layouts/wrapper/page-content';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { Spacer } from 'app/layouts/generic';
import { useOrganizationService } from 'hooks/organization';
import { useAgencySalesService } from 'hooks/sales/agency';
import { useEarningsService } from 'hooks/sales/earnings';
import { useCurrencyService } from 'hooks/sales/currency';
import { toReadableNumber } from 'app/shared/utils/number';
import useScreenSize from 'hooks/size';
import { InvoicesList } from './sections/invoices';
import { PayoutAccount } from './sections/payout';
import { RecentEarnings } from './sections/earnings';
import { ActiveSubscriptions } from '../dashboard/sections/active';

const DEFAULT_CURRENCY = 'NGN';

const Billing = () => {
  const dispatch = useDispatch();
  const { isMobile } = useScreenSize();
  const agency = useSelector((state) => state.metadata.agency);
  const { fetchOrganizations } = useOrganizationService();
  const { fetchInvoiceSummary, fetchActiveSubscriptions } = useAgencySalesService();
  const { fetchEarnings } = useEarningsService();
  const { getCurrencySymbol } = useCurrencyService();

  const is_freelancer = agency?.track === 'freelancer';

  const [invoices, setInvoices] = useState([]);
  const [invoices_loading, setInvoicesLoading] = useState(true);
  const [earnings, setEarnings] = useState([]);
  const [earnings_loading, setEarningsLoading] = useState(is_freelancer);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptions_loading, setSubscriptionsLoading] = useState(!is_freelancer);

  const currency_symbol = getCurrencySymbol(DEFAULT_CURRENCY) || '₦';

  useEffect(() => {
    dispatch(setPageTitle([{ title: 'Billing', path: '.' }]));
    if (!agency?.id) return;

    load();

    // Earnings (and the payout panel) are freelancer-only.
    if (is_freelancer) {
      setEarningsLoading(true);
      fetchEarnings({
        query_string: `agency_id=${agency.id}&status=settled&sort_by=-created_on&page=0&population=5`
      }).then(({ earnings: fetched }) => {
        setEarnings((fetched || []).slice(0, 5));
        setEarningsLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agency]);

  // The agency endpoints are scoped to the agency's orgs, so resolve their ids
  // (and names) once, then load invoices and — for businesses — active subs.
  const load = async () => {
    setInvoicesLoading(true);
    if (!is_freelancer) setSubscriptionsLoading(true);

    const { orgs } = await fetchOrganizations({ query_string: `agent_id=${agency.id}` });
    const list = orgs || [];
    const org_ids = list.map((org) => org.id).join(',');
    const org_names = list.reduce((map, org) => ({ ...map, [org.id]: org.name }), {});

    fetchInvoiceSummary({ query_string: `org_ids=${org_ids}` }).then(({ paid, unpaid }) => {
      const combined = [
        ...(paid || []).map((invoice) => ({ ...invoice, status: 'paid' })),
        ...(unpaid || []).map((invoice) => ({ ...invoice, status: 'unpaid' }))
      ].filter((invoice) => invoice?.metadata?.for === 'customer');
      setInvoices(combined);
      setInvoicesLoading(false);
    });

    if (!is_freelancer) {
      fetchActiveSubscriptions({ query_string: `org_ids=${org_ids}` }).then(({ subscriptions }) => {
        setSubscriptions((subscriptions || []).map((sub) => toSubscriptionItem(sub, org_names)));
        setSubscriptionsLoading(false);
      });
    }
  };

  const money = (amount, code) =>
    amount == null
      ? null
      : `${getCurrencySymbol(code) || currency_symbol}${toReadableNumber(amount)}`;

  const toSubscriptionItem = (sub, org_names) => {
    const amount = sub.price?.amount ?? sub.cost;
    const currency_code = sub.price?.currency?.code ?? sub.currency?.code ?? sub.currency;
    return {
      id: sub.id,
      client_name: org_names[sub.tenant_id] || sub.tenant?.name || 'Client',
      plan: sub.title || sub.tier || 'Subscription',
      cost_label: amount != null ? money(amount, currency_code) : null,
      expires_label: sub.expires_at
        ? new Date(sub.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : null
    };
  };

  return (
    <PageContentWrapper>
      {isMobile && <Spacer multiple={2} />}

      <GridRow num_of_columns={5} mobileStyles={{ gap: 0 }}>
        <GridColumn span={3}>
          <InvoicesList items={invoices} loading={invoices_loading} />
        </GridColumn>

        <GridColumn span={2}>
          {is_freelancer ? (
            <>
              <PayoutAccount />
              <Spacer multiple={3} />
              <RecentEarnings items={earnings} loading={earnings_loading} />
            </>
          ) : (
            <ActiveSubscriptions items={subscriptions} loading={subscriptions_loading} />
          )}
        </GridColumn>
      </GridRow>

      <Spacer multiple={4} />
    </PageContentWrapper>
  );
};

export default Billing;
